import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import { Box, Card, Typography } from "@mui/material";
import type {
  MsmeAuthFeatureColorScheme,
  MsmeAuthFeatureIcon,
  MsmeAuthHighlightItem,
} from "../../../../content/msmeAuthMarketing";
import type { MsmeAuthMarketingPanelProps } from "../types";

const FEATURE_COLOR_THEMES: Record<
  MsmeAuthFeatureColorScheme,
  { bg: string; border: string; iconColor: string }
> = {
  blue: { bg: "#eff6ff", border: "#dbeafe", iconColor: "#2563eb" },
  orange: { bg: "#fff7ed", border: "#ffedd5", iconColor: "#f97316" },
  green: { bg: "#f0fdf4", border: "#dcfce7", iconColor: "#16a34a" },
  purple: { bg: "#faf5ff", border: "#f3e8ff", iconColor: "#9333ea" },
};

const FEATURE_ICON_MAP: Record<MsmeAuthFeatureIcon, typeof HubOutlinedIcon> = {
  onboarding: PersonAddOutlinedIcon,
  catalogue: Inventory2OutlinedIcon,
  account: ManageAccountsOutlinedIcon,
  inventory: Inventory2OutlinedIcon,
  logistics: LocalShippingOutlinedIcon,
  visibility: VisibilityOutlinedIcon,
};

function renderFeatureIcon(iconKey: MsmeAuthFeatureIcon, color: string) {
  const Icon = FEATURE_ICON_MAP[iconKey] ?? HubOutlinedIcon;
  return <Icon sx={{ fontSize: 16, color }} />;
}

const HIGHLIGHT_TONE_CONFIG: Record<
  MsmeAuthHighlightItem["tone"],
  {
    suffix: string;
    titleColor: string;
    Icon: typeof LanguageOutlinedIcon;
    iconColor: string;
  }
> = {
  info: {
    suffix: ".",
    titleColor: "rgba(30, 58, 138, 0.9)",
    Icon: LanguageOutlinedIcon,
    iconColor: "#0ea5e9",
  },
  success: {
    suffix: " directly into your dashboard queue.",
    titleColor: "rgba(21, 128, 61, 0.9)",
    Icon: ShoppingCartOutlinedIcon,
    iconColor: "#16a34a",
  },
};

const ColoredCardsPanel: React.FC<MsmeAuthMarketingPanelProps> = ({
  content,
  subheadline,
}) => {
  const { title, features, highlights } = content;

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        maxWidth: 460,
        mx: "auto",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: "100%",
          pb: 1.5,
          textAlign: "center",
        }}
      >
        <Typography
          component="h1"
          sx={{
            fontWeight: 700,
            fontSize: "clamp(18px, 2.2vw, 24px)",
            lineHeight: "150%",
            letterSpacing: 0,
            mb: "2px",
          }}
        >
          {title}
        </Typography>
        {subheadline ? (
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: "clamp(12px, 1.3vw, 13px)",
              pt: 0.5,
              lineHeight: "150%",
              color: "rgba(0, 0, 0, 0.4)",
            }}
          >
            {subheadline}
          </Typography>
        ) : null}
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          width: "100%",
        }}
      >
        {features.map((feature) => {
          const theme = FEATURE_COLOR_THEMES[feature.colorScheme];

          return (
            <Card
              key={feature.title}
              sx={{
                backgroundColor: theme.bg,
                border: `1px solid ${theme.border}`,
                borderRadius: "10px",
                p: "13px",
                boxShadow: "none",
                transition: "box-shadow 0.2s ease, transform 0.2s ease",
                "&:hover": {
                  boxShadow:
                    "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)",
                  transform: "translateY(-2px)",
                },
              }}
            >
              <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: "6px",
                    bgcolor: "#ffffff",
                    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    mt: "1px",
                  }}
                >
                  {renderFeatureIcon(feature.icon, theme.iconColor)}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: "clamp(12px, 1.3vw, 13px)",
                      mb: 0.5,
                      lineHeight: "150%",
                      letterSpacing: 0,
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: "clamp(11px, 1.15vw, 12px)",
                      lineHeight: "150%",
                      letterSpacing: 0,
                      color: "rgba(0, 0, 0, 0.5)",
                    }}
                  >
                    {feature.description}
                  </Typography>
                </Box>
              </Box>
            </Card>
          );
        })}

        {highlights.map((item) => {
          const { Icon, iconColor, titleColor, suffix } = HIGHLIGHT_TONE_CONFIG[item.tone];
          return (
            <Card
              key={item.title}
              sx={{
                backgroundColor: "#ffffff",
                border: "1px solid #f3f4f6",
                borderRadius: "10px",
                p: "13px",
                boxShadow: "none",
              }}
            >
              <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
                <Icon sx={{ fontSize: 16, color: iconColor, mt: "2px", flexShrink: 0 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: "clamp(12px, 1.3vw, 13px)",
                      lineHeight: "150%",
                      letterSpacing: 0,
                      color: titleColor,
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: "clamp(11px, 1.15vw, 12px)",
                      lineHeight: "150%",
                      letterSpacing: 0,
                      color: "rgba(0, 0, 0, 0.5)",
                      mt: 0.5,
                    }}
                  >
                    {item.description}{" "}
                    <Box component="span" sx={{ fontWeight: 700, color: "rgba(0, 0, 0, 0.65)" }}>
                      {item.emphasis}
                    </Box>
                    {suffix}
                  </Typography>
                </Box>
              </Box>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
};

export default ColoredCardsPanel;
